import { Analytics, trackClientError } from '../analytics/analytics';
import { AnalyticsDebugSink, type RecordedAnalyticsEvent } from '../analytics/debugSink';
import { SessionMilestoneTracker } from '../onboarding/sessionMilestones';
import { platform } from '../platform/platform';

export interface PlayableAnalyticsRuntime {
  readonly sink: AnalyticsDebugSink;
  readonly milestones: SessionMilestoneTracker;
  updateMilestones(elapsedMs?: number): void;
  elapsedMs(at?: number): number;
  events(): RecordedAnalyticsEvent[];
  detach(): void;
}

type SessionListener = (event: Event) => void;

export interface ProductionSessionHost {
  addEventListener(type: string, listener: SessionListener): void;
  removeEventListener(type: string, listener: SessionListener): void;
  document: {
    visibilityState: string;
    addEventListener(type: string, listener: SessionListener): void;
    removeEventListener(type: string, listener: SessionListener): void;
  };
}

export interface ProductionSessionWatch {
  /** Once, when the production session and HUD are up, including the degraded WebGL path. */
  markReady(): void;
  /** webgl_context_lost only. Call only when a canvas exists. */
  attachCanvas(canvas: { addEventListener(type: string, listener: SessionListener): void }): void;
  detach(): void;
}

/**
 * Shared playable-runtime analytics seam.
 * Domain events stay on GameSession. This starts game_start and session milestones.
 * Production lifecycle events are attached separately and do not start a second runtime.
 */
export function startPlayableAnalytics(now = Date.now()): PlayableAnalyticsRuntime {
  const sink = new AnalyticsDebugSink();
  const detach = Analytics.addDebugListener((name, payload) => {
    sink.record(name, payload);
  });
  const milestones = new SessionMilestoneTracker();
  const startedAt = now;
  Analytics.track('game_start', { platform: platform.id });

  return {
    sink,
    milestones,
    updateMilestones(elapsedMs = Date.now() - startedAt) {
      milestones.update(elapsedMs);
    },
    elapsedMs(at = Date.now()) {
      return Math.max(0, at - startedAt);
    },
    events() {
      return sink.events.slice();
    },
    detach,
  };
}

function safe(run: () => void): void {
  try {
    run();
  } catch {
    // Client-error and lifecycle listeners must not rethrow.
  }
}

/**
 * Production lifecycle events around an already-started playable runtime.
 * Does not call startPlayableAnalytics.
 */
export function bindProductionSessionWatch(
  runtime: PlayableAnalyticsRuntime,
  host: ProductionSessionHost,
): ProductionSessionWatch {
  let ready = false;
  let inputFired = false;
  let ended = false;
  let canvasAttached = false;

  const endSession = (): void => {
    if (ended) {
      return;
    }
    ended = true;
    Analytics.track('session_end', { elapsedMs: runtime.elapsedMs() });
  };

  const onPageHide: SessionListener = () => {
    safe(endSession);
  };

  const onVisibility: SessionListener = () => {
    safe(() => {
      if (host.document.visibilityState === 'hidden') {
        endSession();
      }
    });
  };

  const onError: SessionListener = (event) => {
    safe(() => {
      const message = typeof (event as ErrorEvent).message === 'string' ? (event as ErrorEvent).message : '';
      trackClientError('window_error', message);
    });
  };

  const onRejection: SessionListener = (event) => {
    safe(() => {
      trackClientError('unhandled_rejection', (event as PromiseRejectionEvent).reason);
    });
  };

  const onFirstInput: SessionListener = () => {
    safe(() => {
      if (!ready || inputFired) {
        return;
      }
      inputFired = true;
      Analytics.track('first_input');
    });
  };

  host.addEventListener('pagehide', onPageHide);
  host.document.addEventListener('visibilitychange', onVisibility);
  host.addEventListener('error', onError);
  host.addEventListener('unhandledrejection', onRejection);
  host.addEventListener('pointerdown', onFirstInput);
  host.addEventListener('keydown', onFirstInput);

  return {
    markReady() {
      if (ready) {
        return;
      }
      ready = true;
      Analytics.track('game_ready');
    },
    attachCanvas(canvas) {
      if (canvasAttached) {
        return;
      }
      canvasAttached = true;
      canvas.addEventListener('webglcontextlost', () => {
        safe(() => {
          trackClientError('webgl_context_lost', '');
        });
      });
    },
    detach() {
      host.removeEventListener('pagehide', onPageHide);
      host.document.removeEventListener('visibilitychange', onVisibility);
      host.removeEventListener('error', onError);
      host.removeEventListener('unhandledrejection', onRejection);
      host.removeEventListener('pointerdown', onFirstInput);
      host.removeEventListener('keydown', onFirstInput);
    },
  };
}
